import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap'
import client from '../../api/client'

function PollForm() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)

  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    title: '',
    description: '',
    context_type: 'general',
    context_value: '',
    active: false,
    starts_at: '',
    ends_at: '',
    questions: []
  })

  useEffect(() => {
    if (isEdit) {
      fetchPoll()
    }
  }, [id])

  const fetchPoll = async () => {
    try {
      const response = await client.get(`/admin/api/v1/polls/${id}`)
      const poll = response.data
      setForm({
        title: poll.title || '',
        description: poll.description || '',
        context_type: poll.context_type || 'general',
        context_value: poll.context_value || '',
        active: poll.active || false,
        starts_at: poll.starts_at ? poll.starts_at.substring(0, 16) : '',
        ends_at: poll.ends_at ? poll.ends_at.substring(0, 16) : '',
        questions: poll.questions || []
      })
    } catch (err) {
      console.error('Error fetching poll:', err)
      setError('Erreur lors du chargement du sondage')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!form.title.trim()) {
      setError('Le titre est requis')
      return
    }

    if (form.questions.length === 0) {
      setError('Le sondage doit contenir au moins une question')
      return
    }

    setSaving(true)
    try {
      const payload = {
        ...form,
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
        context_value: form.context_type === 'general' ? null : form.context_value
      }

      if (isEdit) {
        await client.put(`/admin/api/v1/polls/${id}`, payload)
      } else {
        await client.post('/admin/api/v1/polls', payload)
      }

      navigate('/polls')
    } catch (err) {
      console.error('Error saving poll:', err)
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde du sondage')
      setTimeout(() => setError(''), 5000)
    } finally {
      setSaving(false)
    }
  }

  const addQuestion = () => {
    setForm({
      ...form,
      questions: [
        ...form.questions,
        {
          question_text: '',
          question_type: 'rating',
          options: [],
          required: true,
          display_order: form.questions.length
        }
      ]
    })
  }

  const removeQuestion = (index) => {
    const newQuestions = form.questions.filter((_, i) => i !== index)
    setForm({ ...form, questions: newQuestions })
  }

  const updateQuestion = (index, field, value) => {
    const newQuestions = [...form.questions]
    newQuestions[index] = { ...newQuestions[index], [field]: value }
    setForm({ ...form, questions: newQuestions })
  }

  const updateQuestionOptions = (index, optionsText) => {
    const options = optionsText.split('\n').filter(o => o.trim())
    updateQuestion(index, 'options', options)
  }

  const getQuestionTypeLabel = (type) => {
    const labels = {
      rating: 'Note (1-5)',
      text: 'Texte libre',
      choice: 'Choix multiple'
    }
    return labels[type] || type
  }

  if (loading) {
    return (
      <Container fluid>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
        </div>
      </Container>
    )
  }

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2>{isEdit ? '✏️ Modifier le sondage' : '➕ Créer un sondage'}</h2>
          <p className="text-muted">
            {isEdit ? 'Modifiez les informations du sondage' : 'Créez un nouveau sondage pour collecter les avis utilisateurs'}
          </p>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Card className="mb-3">
          <Card.Header>
            <strong>Informations générales</strong>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Titre *</Form.Label>
              <Form.Control
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ex: Satisfaction sur la fonctionnalité Chat"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Description optionnelle du sondage"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Type de contexte *</Form.Label>
                  <Form.Select
                    value={form.context_type}
                    onChange={(e) => setForm({ ...form, context_type: e.target.value, context_value: '' })}
                    required
                  >
                    <option value="general">Général</option>
                    <option value="page">Page</option>
                    <option value="feature">Fonctionnalité</option>
                    <option value="model">Modèle</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Où ce sondage doit-il apparaître ?
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                {form.context_type !== 'general' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Valeur du contexte *</Form.Label>
                    <Form.Control
                      type="text"
                      value={form.context_value}
                      onChange={(e) => setForm({ ...form, context_value: e.target.value })}
                      placeholder={
                        form.context_type === 'page' ? '/pricing' :
                        form.context_type === 'feature' ? 'chat' :
                        form.context_type === 'model' ? 'gpt-4' : ''
                      }
                      required
                    />
                    <Form.Text className="text-muted">
                      {form.context_type === 'page' && 'Ex: /pricing, /dashboard'}
                      {form.context_type === 'feature' && 'Ex: chat, image-generation'}
                      {form.context_type === 'model' && 'Ex: gpt-4, claude-3'}
                    </Form.Text>
                  </Form.Group>
                )}
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="active-switch"
                    label={form.active ? "Sondage actif" : "Sondage inactif"}
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Date de début</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={form.starts_at}
                    onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Date de fin</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={form.ends_at}
                    onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <strong>Questions</strong>
            <Button variant="success" size="sm" onClick={addQuestion}>
              + Ajouter une question
            </Button>
          </Card.Header>
          <Card.Body>
            {form.questions.length === 0 ? (
              <div className="text-center py-4 text-muted">
                Aucune question. Cliquez sur "Ajouter une question" pour commencer.
              </div>
            ) : (
              form.questions.map((question, index) => (
                <Card key={index} className="mb-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <Badge bg="secondary">Question {index + 1}</Badge>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeQuestion(index)}
                      >
                        Supprimer
                      </Button>
                    </div>

                    <Form.Group className="mb-3">
                      <Form.Label>Texte de la question *</Form.Label>
                      <Form.Control
                        type="text"
                        value={question.question_text}
                        onChange={(e) => updateQuestion(index, 'question_text', e.target.value)}
                        placeholder="Ex: Comment évaluez-vous cette fonctionnalité ?"
                        required
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Type de question *</Form.Label>
                          <Form.Select
                            value={question.question_type}
                            onChange={(e) => updateQuestion(index, 'question_type', e.target.value)}
                            required
                          >
                            <option value="rating">Note (1-5)</option>
                            <option value="text">Texte libre</option>
                            <option value="choice">Choix multiple</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Check
                            type="switch"
                            id={`required-${index}`}
                            label="Réponse obligatoire"
                            checked={question.required}
                            onChange={(e) => updateQuestion(index, 'required', e.target.checked)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    {question.question_type === 'choice' && (
                      <Form.Group className="mb-3">
                        <Form.Label>Options de réponse (une par ligne) *</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={(question.options || []).join('\n')}
                          onChange={(e) => updateQuestionOptions(index, e.target.value)}
                          placeholder="Option 1&#10;Option 2&#10;Option 3"
                          required
                        />
                        <Form.Text className="text-muted">
                          {(question.options || []).length} option(s) configurée(s)
                        </Form.Text>
                      </Form.Group>
                    )}

                    <div className="mt-2">
                      <small className="text-muted">
                        Type: {getQuestionTypeLabel(question.question_type)} |
                        {question.required ? ' Obligatoire' : ' Optionnelle'}
                      </small>
                    </div>
                  </Card.Body>
                </Card>
              ))
            )}
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={() => navigate('/polls')}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving || form.questions.length === 0}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Enregistrement...
              </>
            ) : (
              isEdit ? 'Enregistrer les modifications' : 'Créer le sondage'
            )}
          </Button>
        </div>
      </Form>
    </Container>
  )
}

export default PollForm
